import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import type { AuthMeData } from '../types/api';
import { fetchMe, logout as apiLogout } from '../api/auth';
import { setUnauthorizedHandler } from '../api/client';

type Status = 'loading' | 'authed' | 'anon';

interface AuthState {
  status: Status;
  user: AuthMeData | null;
  /** 使用者主動登出。 */
  logout: () => Promise<void>;
  /** 強制重新打 /api/auth/me。 */
  refresh: () => Promise<void>;
}

const AuthCtx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>('loading');
  const [user, setUser] = useState<AuthMeData | null>(null);

  const refresh = useCallback(async () => {
    try {
      const me = await fetchMe();
      setUser(me);
      setStatus('authed');
    } catch {
      setUser(null);
      setStatus('anon');
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiLogout();
    } catch {
      // 即使 server 端失敗也視為已登出（cookie 可能已失效）
    }
    setUser(null);
    setStatus('anon');
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // 任何 API 呼叫遇到 401 → 直接設為 anon（讓 ProtectedRoute 跳走）
  useEffect(() => {
    setUnauthorizedHandler(() => {
      setUser(null);
      setStatus('anon');
    });
    return () => setUnauthorizedHandler(null);
  }, []);

  return <AuthCtx.Provider value={{ status, user, logout, refresh }}>{children}</AuthCtx.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
